import React from 'react';
import { AbsoluteFill, interpolate, useCurrentFrame, useVideoConfig } from 'remotion';
import { FONTS } from './theme';
import { useLayout } from './ui';

/**
 * Split narration into short caption chunks: sentence/clause breaks first, then at
 * most ~7 words per chunk so a line stays readable on a phone.
 */
export function captionChunks(text: string, maxWords = 7): string[] {
    const out: string[] = [];
    for (const part of text.replace(/\s+/g, ' ').trim().split(/(?<=[.!?,;:])\s+/)) {
        const words = part.split(' ').filter(Boolean);
        const groups = Math.ceil(words.length / maxWords);
        // Even groups ("6+5" rather than "7+4") so no line ends with one stray word.
        const size = Math.ceil(words.length / Math.max(1, groups));
        for (let i = 0; i < words.length; i += size) out.push(words.slice(i, i + size).join(' '));
    }
    return out;
}

/**
 * Narration subtitles. No word timings come back from TTS, so each chunk gets a
 * share of the clip length proportional to its character count (close enough for
 * natural speech).
 */
export const Captions: React.FC<{ text: string; durationSec: number }> = ({ text, durationSec }) => {
    const frame = useCurrentFrame();
    const { fps } = useVideoConfig();
    const chunks = captionChunks(text);
    if (!chunks.length) return null;
    const total = chunks.reduce((n, c) => n + c.length + 4, 0);
    const t = frame / fps;
    let acc = 0;
    let idx = chunks.length - 1;
    let start = 0;
    for (let i = 0; i < chunks.length; i++) {
        const d = ((chunks[i].length + 4) / total) * durationSec;
        if (t < acc + d) {
            idx = i;
            start = acc;
            break;
        }
        acc += d;
    }
    if (t > durationSec + 0.3) return null;
    return <CaptionBox text={chunks[idx]} key={idx} sinceFrames={(t - start) * fps} />;
};

/** One caption line at the bottom (above the Reels/TikTok UI in portrait). */
export const CaptionBox: React.FC<{ text: string; sinceFrames: number }> = ({ text, sinceFrames }) => {
    const { u, portrait } = useLayout();
    const opacity = interpolate(sinceFrames, [0, 4], [0, 1], { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' });
    return (
        <AbsoluteFill style={{ justifyContent: 'flex-end', alignItems: 'center', paddingBottom: (portrait ? 230 : 70) * u, paddingLeft: 60 * u, paddingRight: 60 * u, pointerEvents: 'none' }}>
            <div
                style={{
                    maxWidth: portrait ? '90%' : '75%',
                    padding: `${10 * u}px ${22 * u}px`,
                    borderRadius: 14 * u,
                    background: 'rgba(0,0,0,0.62)',
                    color: '#FFFFFF',
                    fontFamily: FONTS.sans,
                    fontWeight: 700,
                    fontSize: (portrait ? 46 : 42) * u,
                    lineHeight: 1.3,
                    textAlign: 'center',
                    opacity,
                    transform: `translateY(${(1 - opacity) * 8 * u}px)`,
                }}
            >
                {text}
            </div>
        </AbsoluteFill>
    );
};

/** Captions of the original speech in a clip, timed in source seconds (handles trims). */
export const SpeechCaptions: React.FC<{ segments: { start: number; end: number; text: string }[]; trimStart: number }> = ({ segments, trimStart }) => {
    const frame = useCurrentFrame();
    const { fps } = useVideoConfig();
    const t = trimStart + frame / fps;
    const seg = segments.find((x) => t >= x.start && t < x.end + 0.15);
    if (!seg) return null;
    return <CaptionBox key={seg.start} text={seg.text} sinceFrames={(t - seg.start) * fps} />;
};
